import {useQuery} from "@apollo/client";
import {ProductTypeEnum} from "API";
import {AddToCartButton} from 'components/Button/Button';
import InputIncDec from 'components/InputIncDec/InputIncDec';
import CarouselWithCustomDots from 'components/MultiCarousel/MultiCarousel';
import TWProductCard from "components/ProductCard/TWProductCard";
import ShareButton from "components/shop/ShareButton";
import Tooltip from "components/Tooltip";
import {DescriptionContainer} from "containers/ProductDetails/ProductDetails.style";
import TripDatesModal from "containers/TripDatesModal/TripDatesModal";
import {CartContext} from 'contexts/cart/cart.context';
import {ModalContext} from "contexts/modal.provider";
import {StockItemContext} from "contexts/stock/stock.provider";
import gql from "graphql-tag";
import {listCrossSellByParentId} from "graphql/query/product.query";
import {mdToHtml} from "helper/docs/markdown-to-html";
import {getProductDetailsViewed} from "helper/googleTags";
import {cartAnimation, findProductIndex, getProductQuantity} from 'helper/utility';
import Head from "next/head";
import Link from "next/link";
import Router from 'next/router';
import React, {useContext, useEffect, useState} from 'react';
import {Scrollbars} from 'react-custom-scrollbars';
import notifications from "utils/notifications";

import {
    ModalClose,
    ProductCartBtn,
    ProductDetailsWrapper,
    ProductInfo,
    ProductInfoWrapper,
    ProductPreview,
    ProductTitle,
    ProductWeight,
    QuickViewWrapper,
} from './QuickView.style';

type QuickViewProps = {
    item: any;
    onModalClose: any;
};

function DesktopScroll({children}) {
    return <Scrollbars universal autoHide autoHeight autoHeightMax='90vh'>{children}</Scrollbars>;
}

const QuickView: React.FunctionComponent<QuickViewProps> = ({
                                                                item,
                                                                onModalClose,
                                                            }) => {
    const [pendingAdd, setPendingAdd] = useState(null);
    const [descr, setDescr] = useState(null);
    const [deposit, setDeposit] = useState(null);
    const {
        add,
        getProductTripTotal,
        getProduct,
        getProductPrice,
        update,
        cart: {stockLocation, products, orderStartTime, orderEndTime, tripDays, tripDiscountMultiplier}
    } = useContext(CartContext);
    const {getAvailableQty} = useContext(StockItemContext);
    const [stockItems, setStockItems] = useState(null);
    const [crossSellProducts, setCrossSellProducts] = useState([]);
    const {openModal, closeModal} = useContext(ModalContext);

    const crossSellQuery = useQuery(gql(listCrossSellByParentId), {
        skip: !item,
        variables: {
            company: 'scuttle',
            parentId: {
                eq: item?.id
            },
        }
    })

    const {
        id,
        name,
        unit,
        price,
        description,
        media,
        categories,
        settings
    } = item;

    useEffect(() => {
        if (orderStartTime && orderEndTime) {
            getAvailableQty(item.id, orderStartTime, orderEndTime, stockLocation)
                .then((rsp) => {
                    setStockItems(rsp);
                    if (pendingAdd) {
                        if (rsp > 0) {
                            document.getElementById(`quickview-add-${item.id}`).click();
                        } else {
                            notifications.info(`There are no available stock for your selected dates`, 8000);
                        }
                        setPendingAdd(null);
                    }
                });
        }
    }, [orderStartTime, orderEndTime]);

    useEffect(() => {
        if (description) {
            mdToHtml(description).then(setDescr)
        }
    }, [description]);


    useEffect(() => {
        if (item) {
            (window as any).dataLayer?.push(getProductDetailsViewed(item))
        }
    }, [item]);


    useEffect(() => {
        if (!crossSellQuery.loading && crossSellQuery.data) {
            setCrossSellProducts(crossSellQuery.data.listCrossSellByParentId.items.filter(p => !!p.product).map(p => p.product))
        }
    }, [crossSellQuery]);

    useEffect(() => {
        if (settings) {
            setDeposit(settings.items.find(s => s.settingId === 'deposit')?.value);
        }
    }, [settings]);


    const index = findProductIndex(products, id);
    const quantity = getProductQuantity(products, index);

    const handleClick = (e: any) => {
        e.stopPropagation();
        if (!orderStartTime) {
            setPendingAdd(true);
            openModal({
                content: <TripDatesModal onOk={closeModal}/>,
                classes: 'overflow-visible'
            });
        } else {
            const image = item.image ?? (item.media && item.media.items[0] ? item.media.items[0].media : null);
            if (image) {
                cartAnimation(e);
            }
            add(item);
        }
    };

    const handleUpdate = (value: number, e: any) => {
        if (index === -1 && value === 1) {
            add(item);
        } else {
            update(id, value);
        }
    };

    function onCategoryClick(slug) {
        Router.push({
            pathname: `/`,
            query: {category: slug},
        }).then(() => window.scrollTo(0, 0));
        onModalClose();
    }

    return (
        <>
            <Head>
                {/*<script type="text/javascript" src="//s7.addthis.com/js/300/addthis_widget.js#pubid=ra-5f76fcb850653cce"></script>*/}
            </Head>
            <QuickViewWrapper>
                <DesktopScroll>
                    <div className="flex w-full justify-end">
                        <ModalClose className="text-gray-400" onClick={onModalClose}>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className="w-5 h-5"
                                viewBox='0 0 10.003 10'
                            >
                                <path
                                    data-name='_ionicons_svg_ios-close (5)'
                                    d='M166.686,165.55l3.573-3.573a.837.837,0,0,0-1.184-1.184l-3.573,3.573-3.573-3.573a.837.837,0,1,0-1.184,1.184l3.573,3.573-3.573,3.573a.837.837,0,0,0,1.184,1.184l3.573-3.573,3.573,3.573a.837.837,0,0,0,1.184-1.184Z'
                                    transform='translate(-160.5 -160.55)'
                                    fill='currentColor'
                                />
                            </svg>
                        </ModalClose>
                    </div>
                    <ProductDetailsWrapper className='product-card' dir='ltr'>
                        <ProductPreview>
                            <CarouselWithCustomDots
                                items={media.items.map(a => a.media).filter(m => m)}
                            />
                        </ProductPreview>
                        <ProductInfoWrapper dir={'ltr'}>
                            <div>
                                <ProductInfo>
                                    <div className="flex flex-wrap items-center justify-between">
                                        <ProductTitle>
                                            <Link href={`/product/${item.id}`} prefetch={false}>
                                                <a className="cursor-pointer" target="_blank">
                                                    {name}
                                                </a>
                                            </Link>
                                        </ProductTitle>
                                        <ShareButton
                                            className="hidden sm:flex"
                                            title={item.name}
                                            url={`https://www.scuttle.co.za/rent/product/${item.id}`}
                                        />
                                    </div>
                                    <div className="flex items-left text-left">
                                        <ShareButton
                                            className="flex sm:hidden"
                                            title={item.name}
                                            url={`https://www.scuttle.co.za/rent/product/${item.id}`}
                                        />
                                    </div>


                                    {/*Item is discounted - line through the original price*/}
                                    {tripDiscountMultiplier < 1 && <div className="mt-2 flex items-center">
                                    <span className="line-through text-red-500 font-bold">
                                    <h4 className="flex mt-1 text-gray-400 font-semibold text-lg">R{(price).toFixed(2)}
                                        {item.type !== ProductTypeEnum.RETAIL_ITEM && <span
                                            className="inline-flex ml-1 text-sm text-gray-600 no-underline">/day</span>}
                                    </h4>
                                    </span>
                                    </div>}
                                    <div className="mt-2 flex items-center">
                                    <span
                                        className="inline-block px-2 py-1 leading-none bg-green-200 text-green-800 rounded font-semibold tracking-wide text-md">
                                        R {getProductPrice(tripDays, price, item.type).toFixed(2)} {item.type !== ProductTypeEnum.RETAIL_ITEM && '/day'}
                                    </span>

                                        {deposit &&
                                        <div
                                            className="ml-2 text-md text-gray-600 font-semibold tracking-wide flex items-center">
                                        <span
                                            className="ml-2 inline-block px-2 py-1 leading-none bg-blue-200 text-blue-800 rounded font-semibold tracking-wide text-md">
                                        R {getProductTripTotal(item, getProduct(item.id)?.qty).depositTotal.toFixed(2)} deposit
                                        </span>
                                            <Tooltip
                                                text={'We charge a refundable deposit which will be refunded to you after checking your returned products.'}/>

                                        </div>}
                                    </div>

                                    <div className="flex items-center">

                                        {tripDays > 0 &&
                                        <div className="my-1 text-md text-gray-700 font-semibold tracking-wide">
                                            Total:
                                            R {getProductTripTotal(item, getProduct(item.id)?.qty).total.toFixed(2)}
                                        </div>}
                                    </div>

                                    {
                                        getProduct(item.id)?.qty >= stockItems &&
                                        <div className="w-full text-red-500 text-xs tracking-wide">
                                            Contact us to book {stockItems + 1}+
                                        </div>
                                    }

                                    {stockItems === 0 ?

                                        <span
                                            className="inline-flex items-center px-2 py-0.5 rounded text-sm font-semibold leading-4 bg-orange-100 text-orange-800">
                              Out of stock for your dates
                            </span>
                                        :

                                        <ProductCartBtn className="mt-4">
                                            {quantity <= 0 ? (
                                                <div id={`quickview-add-${item.id}`}>
                                                    <AddToCartButton onClick={handleClick}/>
                                                </div>
                                            ) : (
                                                <InputIncDec
                                                    disabled={quantity >= stockItems}
                                                    value={quantity}
                                                    onUpdate={(value: number, e: any) =>
                                                        handleUpdate(value, e)
                                                    }
                                                />
                                            )}
                                        </ProductCartBtn>
                                    }
                                    <ProductWeight>{unit}</ProductWeight>
                                    <DescriptionContainer>
                                        <div dangerouslySetInnerHTML={{__html: descr}}/>
                                    </DescriptionContainer>

                                </ProductInfo>
                            </div>
                        </ProductInfoWrapper>
                    </ProductDetailsWrapper>
                    {crossSellProducts.length > 0 &&
                    <div className="py-6 xl:flex-1 ">

                        <div className="mt-0 mb-6 md:px-8">
                            <div className="flex justify-between">
                                <div className="pl-2">
                                    <h3 className="text-gray-900 text-xl">Related Products</h3>
                                    <p className="text-gray-600">Make your trip even more memorable by adding these
                                        products:</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap mt-4">
                                {crossSellProducts?.map((product) => {

                                        return (<>
                                                <div
                                                    className={`product-meta product-card bg-white px-2 py-2 w-1/2 sm:1/4 md:w-1/5 xl:1/6`}>
                                                    <TWProductCard
                                                        key={product.id}
                                                        product={product}
                                                    />
                                                </div>
                                            </>
                                        )
                                    }
                                )}
                            </div>
                        </div>


                    </div>}
                </DesktopScroll>

            </QuickViewWrapper>
        </>
    );
};

export default QuickView;
